<template>
  <nav v-if="totalPages" class="nav-pagination front g-24">
    <div>
      <div class="counts row full-width">
        <b>{{ displayedItems > totalItems ? totalItems : displayedItems }}</b>
        {{ $t('pagination.from') }}
        <b>{{ totalItems }}</b>
        <span style="text-transform: lowercase">{{ entity }}</span>
      </div>
      <div class="progress">
        <div class="fill" :style="{width: progressWidth + '%'}"></div>
      </div>
    </div>

    <template v-if="loadMore && currentPage < totalPages">
      <Button ref="loadMoreButton" :always-show-spinner="true" :loading="loading" @click="loadMorePages"
              v-if="$device.isMobile"
              type="black"
              class="tall wide uppercase" :text="$t('pagination.load_more',{count:itemsPerPage,type:entity})"/>
      <Button :loading="loading" @click="loadMorePages" :always-show-spinner="true" v-else
              type="black"
              class="tall wide uppercase" :text="$t('pagination.load_more',{count:itemsPerPage,type:entity})"/>
    </template>

    <div class="nav-pagination-container row">
      <!-- Previous Page Link -->
      <div class="item page-item item-left" v-if="currentPage > 1">
        <nuxt-link :to="prevPageUrl" rel="prev" @click="$emit('change', currentPage-1)">
          <ExpandIcon style="transform: rotate(180deg); margin-top: 7px;"/>
        </nuxt-link>
      </div>

      <!-- Pagination Elements -->
      <div class="pagination g-16 row">
        <nuxt-link v-for="n in paginationRange"
                   :key="n"
                   :class="['page-item', { 'active': n === currentPage }]" @click="$emit('change', n)"
                   :to="generatePageUrl(n)">{{
            n
          }}
        </nuxt-link>
      </div>

      <!-- Next Page Link -->
      <div class="page-item item-right" v-if="currentPage < totalPages">
        <nuxt-link :to="nextPageUrl" rel="next"
                   @click.stop.prevent="$emit('change', currentPage+1)">
          <ExpandIcon style="margin-top: 7px;"/>
        </nuxt-link>
      </div>
    </div>
  </nav>
</template>

<script>
import Button from "~/components/atoms/Button.vue";
import LeftIcon from "~/components/icons/LeftIcon.vue";
import RightIcon from "~/components/icons/RightIcon.vue";
import ExpandIcon from "~/components/icons/ExpandIcon.vue";

export default {
  components: {ExpandIcon, RightIcon, LeftIcon, Button},
  props: {
    currentPage: Number,
    totalPages: Number,
    totalItems: Number,
    count: Number,
    itemsPerPage: Number,
    hasMorePages: Boolean,
    entity: String,
    moreButton: Boolean,
    moreQty: Number,
    loadMore: Boolean,
    loading: Boolean,
    url: String,
  },
  data() {
    const currentPath = this.$route.path;
    return {
      currentPath
    }
  },
  mounted() {
    window.addEventListener('urlReplaced', this.handlePopState);
    this.initIntersectionObserver();
  },
  beforeUnmount() {
    window.removeEventListener('urlReplaced', this.handlePopState);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  computed: {
    getWindowPath() {
      return this.currentPath
    },
    displayedItems() {
      return (this.itemsPerPage * (this.currentPage - 1)) + this.count;
    },
    progressWidth() {
      return (this.displayedItems / this.totalItems) * 100;
    },
    entityName() {
      const names = {product: 'product', collection: 'collection', blog: 'blog'};
      return names[this.entity] || 'item';
    },
    prevPageUrl() {
      return this.currentPage > 1 ? this.generatePageUrl(this.currentPage - 1) : '#';
    },
    nextPageUrl() {
      return this.currentPage < this.totalPages ? this.generatePageUrl(this.currentPage + 1) : '#';
    },
    paginationRange() {
      const rangeStart = Math.max(1, this.currentPage - 2);
      const rangeEnd = Math.min(rangeStart + 4, this.totalPages);
      return Array.from({length: rangeEnd - rangeStart + 1}, (item, index) => rangeStart + index);
    }
  },
  methods: {
    handlePopState() {
      this.currentPath = window.location.pathname;
    },
    generatePageUrl(page) {
      if (process.client) {
        const urlParams = new URLSearchParams(window.location.search);
        if (page === 1) {
          urlParams.delete('page');
        } else {
          urlParams.delete('page');
          urlParams.append('page', page);
        }
        const queryString = urlParams.toString();
        return `${this.currentPath}?${queryString}`;
      } else {
        if (page === 1) {
          return this.currentPath
        } else {
          return `${this.currentPath}?page=${page}`;
        }
      }
    },
    loadMorePages() {
      this.$emit('more', this.currentPage + 1);
    },
    initIntersectionObserver() {
      if (this.$refs.loadMoreButton) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.loadMorePages();
            }
          });
        }, {
          root: null, // null means it uses the viewport as the container
          rootMargin: '0px',
          threshold: 0.1 // Trigger when 10% of the button is visible
        });

        this.observer.observe(this.$refs.loadMoreButton.$el);
      }
    }
  }
}
</script>

<style scoped lang="scss">
.nav-pagination {
  .counts {
    margin-top: 15px;
    color: $black;
    justify-content: center;
    gap: 5px;

    b {
      font-weight: 600;
    }
  }

  &.front {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 30px;
    margin-bottom: 20px;

    .nav-pagination-container {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      gap: 5px;
    }

    .pagination {
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0;
    }

    .progress {
      width: 280px;
      min-height: 2px;
      margin-top: 8px;
      background: $border-grey;
      position: relative;

      .fill {
        width: 100%;
        position: absolute;
        left: 0;
        height: 2px;
        background: $dark-black;
      }
    }

    .page-item {
      margin: 0 5px;
    }

    .page-item, .page-link {
      width: 40px;
      height: 40px;
      min-width: 40px;
      min-height: 40px;
      max-width: 40px;
      max-height: 40px;
      align-items: center;
      display: flex;
      justify-content: center;
      color: $black;
      font-size: 16px;
      font-weight: 500;
      cursor: pointer;
      margin: 0;
      border-radius: 100%;

      &:hover {
        background: $light-grey;
      }

      &.active {
        background: $dark-black;
        color: $white;
        width: 40px;
        height: 40px;

        span {
          color: $white;
        }

        &:hover {
          a, span {
            background: $dark-black;
            color: $white;
          }
        }
      }

      &.disabled {
        a, span {
          cursor: default;
        }

        &:hover {
          background: unset;

          a, span {
            background: unset;
            color: unset;
          }
        }
      }
    }
  }
}
</style>
